import { FC, useMemo } from "react";
import classNames from "classnames";
import { ITransactionResponse } from "@finbackoffice/clientbff-client";
import {
    CurrencyShortNames,
    TransactionOperation,
    getTransactionMethodId,
} from "@finbackoffice/fe-core";
import { formatTransactionMethodId, useCollapseHeight } from "@finbackoffice/site-core";
import { AssetsDirectory, TransactionSource, TranslationScopes } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import { useDate, DEFAULT_DATE_FORMAT } from "hooks";
import { CurrencyFormatter } from "components/base/currency-formater/CurrencyFormater";
import Img from "components/base/img/Img";
import { Svg } from "components/base/svg/Svg";
import styles from "./transaction-row.module.sass";

interface IProps {
    transaction: ITransactionResponse;
}

const TransactionRow: FC<IProps> = ({ transaction }) => {
    const { formatDate } = useDate();
    const { opened, animating, headerRef, containerRef } = useCollapseHeight(false, 160);

    const renderInfoColumn = useMemo(() => {
        switch (transaction.source) {
            case TransactionSource.Casino:
                return transaction.meta?.bet_short_id || "N/A";
            case TransactionSource.Sport:
                return transaction.meta?.ticket_short_id || "N/A";
            case TransactionSource.Bonus:
                if (transaction.meta) {
                    return (
                        <>
                            Timeframe:
                            {` ${
                                transaction.meta.start_date
                                    ? formatDate(
                                          new Date(transaction.meta.start_date),
                                          DEFAULT_DATE_FORMAT,
                                      )
                                    : "N/A"
                            } - ${
                                transaction.meta.end_date
                                    ? formatDate(
                                          new Date(transaction.meta.end_date),
                                          DEFAULT_DATE_FORMAT,
                                      )
                                    : "N/A"
                            }`}
                        </>
                    );
                }
                return null;

            default:
                return null;
        }
    }, [formatDate, transaction.meta, transaction.source]);

    const formatedTransactionMethodId = useMemo(
        () =>
            formatTransactionMethodId(
                getTransactionMethodId(
                    transaction.source,
                    transaction.operation as TransactionOperation,
                ),
            ),
        [transaction.operation, transaction.source],
    );

    return (
        <div className={styles.transactionItem}>
            <div className={styles.row}>
                <span>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${transaction.currency.toLowerCase()}.svg`}
                        className="svg-icon crypto-currency"
                    />
                    {CurrencyShortNames[transaction.currency] || transaction.currency}
                </span>
                <span>
                    <span className={styles.label}>
                        <Translate
                            tid="account_transactions_method"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <Translate
                        tid={`transaction_${formatedTransactionMethodId.replace("-", "_")}`}
                    />
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.MethodIcons}/${formatedTransactionMethodId}.svg`}
                        className="svg-icon"
                    />
                </span>
            </div>
            <div className={styles.currency}>
                <div>
                    <span className={styles.label}>
                        <Translate
                            tid="account_transactions_amount"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <span>
                        <CurrencyFormatter
                            currency={transaction.currency}
                            amount={transaction.amount.toString()}
                        />
                    </span>
                </div>
                <div>
                    <span className={styles.label}>
                        <Translate
                            tid="account_transactions_balance"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :
                    </span>
                    <span>
                        <CurrencyFormatter
                            currency={transaction.currency}
                            amount={transaction.balance.toString()}
                        />
                    </span>
                </div>
            </div>
            <div className={styles.row}>
                <span className={styles.date}>
                    <Img
                        source={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/calendar.svg`}
                        width={14}
                        height={16}
                        alt="calendar"
                    />
                    <span>
                        {formatDate(
                            new Date(transaction.created_at),
                            `${DEFAULT_DATE_FORMAT}, HH:mm`,
                        )}
                    </span>
                </span>
                <span>
                    <span className={styles.label}>
                        <Translate
                            tid="account_transactions_transactionId"
                            namespace={TranslationScopes.UserAccount}
                        />
                        {": "}
                    </span>
                    {transaction.id}
                </span>
            </div>
            {(opened || animating) && (
                <div className={styles.details} ref={containerRef}>
                    <div>
                        <span className={styles.label}>
                            <Translate
                                tid="account_transactions_details"
                                namespace={TranslationScopes.UserAccount}
                            />
                            :
                        </span>
                        <span>{renderInfoColumn}</span>
                    </div>
                    {!!transaction.meta?.free_round_id && (
                        <div>
                            <span className={styles.label}>
                                <Translate
                                    tid="account_bets_promo_id"
                                    namespace={TranslationScopes.UserAccount}
                                />
                                :
                            </span>
                            <span>{transaction.meta?.free_round_id || ""}</span>
                        </div>
                    )}
                </div>
            )}
            <span
                className={classNames(styles.detailsBtn, (opened || animating) && styles.opened)}
                ref={headerRef}>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/arrow-white.svg`}
                    className="svg-icon"
                />
            </span>
        </div>
    );
};

export default TransactionRow;
